import React, { useState, useEffect } from "react";
import { createCourse, searchCourse, updateCourse } from "../../services/CourseService";

const initialCourseData = {
  courseName: "",
  courseCode: "",
  cricosCode:"",
  description: "",
  duration: "",
  instructor: "",
  capacity: "",
  startDate: "",
  endDate: "",
  status: "active",
  requirements: "",
  notes: "",
  fee: "", // keep for backward compatibility
  
  attendanceDetails: {
    Canberra: {
      threeDays: {
        group: "",
        trainer: "",
        room: "",
        term: "",
        day1: "",
        day2: "",
        day3: "",
        totalHours: "20hr / 1hr",
      },
      fiveDays: {
        group: "",
        trainer: "",
        room: "",
        term: "",
        day1: "",
        day2: "",
        day3: "",
        day4: "",
        day5: "",
        totalHours: "20hr / 1hr",
      },
    },
    Melbourne: {
      threeDays: {
        group: "",
        trainer: "",
        room: "",
        term: "",
        day1: "",
        day2: "",
        day3: "",
        totalHours: "20hr / 1hr",
      },
      fiveDays: {
        group: "",
        trainer: "",
        room: "",
        term: "",
        day1: "",
        day2: "",
        day3: "",
        day4: "",
        day5: "",
        totalHours: "20hr / 1hr",
      },
    },
  },

  // New fee structure
  onshoreTuitionFee: "",
  onshoreEnrolmentFee: "",
  onshoreMaterialFee: "",
  onshoreTotalFee: "",

  offshoreTuitionFee: "",
  offshoreEnrolmentFee: "",
  offshoreMaterialFee: "",
  offshoreTotalFee: "",
};
const emptySchedule = {
  group: "",
  trainer: "",
  room: "",
  term: "",
  day1: "",
  day2: "",
  day3: "",
  day4: "",
  day5: "",
  totalHours: "20hr / 1hr",
};

const normalizeAttendanceDetails = (attendanceDetails = {}) => {
  const normalizeCampus = (campusData = {}) => {
    // New format already exists
    if (campusData.threeDays || campusData.fiveDays) {
      return {
        threeDays: {
          ...emptySchedule,
          ...(campusData.threeDays || {}),
        },
        fiveDays: {
          ...emptySchedule,
          ...(campusData.fiveDays || {}),
        },
      };
    }

    // Old format: Canberra.group, Canberra.trainer, Canberra.day1 etc.
    // Copy old saved values into both 3-day and 5-day so old records still work.
    return {
      threeDays: {
        ...emptySchedule,
        ...campusData,
        day4: "",
        day5: "",
      },
      fiveDays: {
        ...emptySchedule,
        ...campusData,
      },
    };
  };

  return {
    Canberra: normalizeCampus(attendanceDetails.Canberra),
    Melbourne: normalizeCampus(attendanceDetails.Melbourne),
  };
};

function CourseForm({ initialData, onCancel }) {
  const [formData, setFormData] = useState(initialCourseData);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        attendanceDetails: normalizeAttendanceDetails(initialData.attendanceDetails),
      }));
    } else {
      setFormData(initialCourseData);
    }
  }, [initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleAttendanceChange = (campus, scheduleType, field, value) => {
    setFormData((prev) => ({
      ...prev,
      attendanceDetails: {
        ...(prev.attendanceDetails || {}),
        [campus]: {
          ...(prev.attendanceDetails?.[campus] || {}),
          [scheduleType]: {
            ...(prev.attendanceDetails?.[campus]?.[scheduleType] || {}),
            [field]: value,
          },
        },
      },
    }));
  };

  // Utility: Calculate the true totals for comparison
  const calculateTotals = () => {
    const onshoreTotal =
      (parseFloat(formData.onshoreEnrolmentFee) || 0) +
      (parseFloat(formData.onshoreMaterialFee) || 0) +
      (parseFloat(formData.onshoreTuitionFee) || 0);

    const offshoreTotal =
      (parseFloat(formData.offshoreEnrolmentFee) || 0) +
      (parseFloat(formData.offshoreMaterialFee) || 0) +
      (parseFloat(formData.offshoreTuitionFee) || 0);

    return {
      onshoreTotal: onshoreTotal.toFixed(2),
      offshoreTotal: offshoreTotal.toFixed(2),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { onshoreTotal, offshoreTotal } = calculateTotals();

      let validationErrors = [];

      if (
        formData.onshoreTotalFee &&
        parseFloat(formData.onshoreTotalFee).toFixed(2) !== onshoreTotal
      ) {
        validationErrors.push(
          `Onshore Total Fee should be ${onshoreTotal} (total of Enrolment + Material + Tuition).`
        );
      }

      if (
        formData.offshoreTotalFee &&
        parseFloat(formData.offshoreTotalFee).toFixed(2) !== offshoreTotal
      ) {
        validationErrors.push(
          `Offshore Total Fee should be ${offshoreTotal} (total of Enrolment + Material + Tuition).`
        );
      }

      if (validationErrors.length > 0) {
        alert(validationErrors.join("\n"));
        return;
      }

      const allCourses = await searchCourse(formData.courseCode);
      const courses = Array.isArray(allCourses)
        ? allCourses
        : allCourses?.data || [];

      const duplicateCourse = courses.find(
        (course) =>
          course.id &&
          course.id !== initialData?.id &&
          course.courseCode?.trim().toLowerCase() ===
            formData.courseCode?.trim().toLowerCase()
      );

      if (duplicateCourse) {
        alert(`Course Code "${duplicateCourse.courseCode}" already exists.`);
        return;
      }

      const payload = {
        ...formData,
        onshoreTotalFee: onshoreTotal,
        offshoreTotalFee: offshoreTotal,
        fee: formData.fee || "",
      };

      if (initialData?.id) {
        await updateCourse(initialData.id, payload);
        alert("Course updated successfully!");
      } else {
        await createCourse(payload);
        alert("Course created successfully!");
      }

      if (onCancel) onCancel();
    } catch (error) {
      console.error("Course save error:", error);
      alert("Failed to save course. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            borderRadius: "12px"
          }}
        >
          <div className="spinner" style={{ textAlign: "center" }}>
            <div className="loader" style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 12px",
              
            }} />
            
          </div>
          <p style={ {
            padding: "0.5rem",
          }
          }>Saving, please wait!</p>
        </div>
      )}
      <form onSubmit={handleSubmit}>
        {/* Header */}
        <div
          className="actions"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginBottom: "1rem",
          }}
        >
          <div>
            <h1 className="page-title">Course Record</h1>
            <p className="page-subtitle">Create and manage Courses</p>
          </div>
          <div style={{ display: "flex", gap: "1rem" }}>
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save Course
            </button>
          </div>
        </div>

        {/* Course Info */}
        <div className="form-section">
          <h2>Course Information</h2>
          <div className="form-grid">
            {[
              { label: "Course Name", name: "courseName", required: true },
              { label: "Course Code", name: "courseCode", required: true },
              { label: "Cricos Code", name: "cricosCode", required: true },
              { label: "Duration (weeks)", name: "duration", type: "number", required: true },
              { label: "Instructor", name: "instructor", required: true },
              { label: "Capacity", name: "capacity", type: "number", required: true },
              { label: "Start Date", name: "startDate", type: "date", required: false },
              { label: "End Date", name: "endDate", type: "date", required: false },
              { label: "Legacy Total Fee ($)", name: "fee", type: "number", required: false },
            ].map(({ label, name, type = "text", required }) => (
              <div key={name} className="form-group">
                <label>{label}</label>
                <input
                  type={type}
                  name={name}
                  value={formData[name]}
                  onChange={handleInputChange}
                  className="form-input"
                  required={required}
                />
              </div>
            ))}

            {/* Status */}
            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-input"
                required
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Onshore Fees */}
            <div className="form-section" style={{ gridColumn: "1 / -1" }}>
              <h2>Onshore Fees</h2>
              <div className="form-grid">
                {[
                  { label: "Enrolment Fee ($)", name: "onshoreEnrolmentFee" },
                  { label: "Material Fee ($)", name: "onshoreMaterialFee" },
                  { label: "Tuition Fee ($)", name: "onshoreTuitionFee" },
                  { label: "Total Fee ($)", name: "onshoreTotalFee" },
                ].map(({ label, name }) => (
                  <div key={name} className="form-group">
                    <label>{label}</label>
                    <input
                      type="number"
                      name={name}
                      value={formData[name]}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Offshore Fees */}
            <div className="form-section" style={{ gridColumn: "1 / -1" }}>
              <h2>Offshore Fees</h2>
              <div className="form-grid">
                {[
                  { label: "Enrolment Fee ($)", name: "offshoreEnrolmentFee" },
                  { label: "Material Fee ($)", name: "offshoreMaterialFee" },
                  { label: "Tuition Fee ($)", name: "offshoreTuitionFee" },
                  { label: "Total Fee ($)", name: "offshoreTotalFee" },
                ].map(({ label, name }) => (
                  <div key={name} className="form-group">
                    <label>{label}</label>
                    <input
                      type="number"
                      name={name}
                      value={formData[name]}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-textarea"
                rows={3}
              />
            </div>

            {/* Requirements and Notes */}
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Requirements</label>
              <textarea
                name="requirements"
                value={formData.requirements}
                onChange={handleInputChange}
                className="form-textarea"
                rows={3}
              />
            </div>
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="form-textarea"
                rows={3}
              />
            </div>
            {/* Attendance Details */}
            <div className="form-section" style={{ gridColumn: "1 / -1" }}>
              <h2>Attendance Sheet Details</h2>

              {["Canberra", "Melbourne"].map((campus) => (
                <div
                  key={campus}
                  className="form-section"
                  style={{
                    gridColumn: "1 / -1",
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    padding: "1rem",
                    marginBottom: "1rem",
                  }}
                >
                  <h3 style={{ marginBottom: "1rem", color: "#192b64" }}>
                    {campus}
                  </h3>

                  {[
                    { key: "threeDays", title: "3 Day Schedule" },
                    { key: "fiveDays", title: "5 Day Schedule" },
                  ].map(({ key: scheduleType, title }) => (
                    <div
                      key={`${campus}-${scheduleType}`}
                      style={{
                        border: "1px solid #dbeafe",
                        borderRadius: 8,
                        padding: "1rem",
                        marginBottom: "1rem",
                        background: "#f8fbff",
                      }}
                    >
                      <h4 style={{ marginBottom: "1rem", color: "#056ec4" }}>
                        {title}
                      </h4>

                      <div className="form-grid">
                        {[
                          { label: "Group", name: "group" },
                          { label: "Trainer", name: "trainer" },
                          { label: "Room", name: "room" },
                          { label: "Term", name: "term" },
                          { label: "Day 1 Timing", name: "day1" },
                          { label: "Day 2 Timing", name: "day2" },
                          { label: "Day 3 Timing", name: "day3" },
                          ...(scheduleType === "fiveDays"
                            ? [
                                { label: "Day 4 Timing", name: "day4" },
                                { label: "Day 5 Timing", name: "day5" },
                              ]
                            : []),
                        ].map(({ label, name }) => (
                          <div key={`${campus}-${scheduleType}-${name}`} className="form-group">
                            <label>{label}</label>
                            <input
                              type="text"
                              className="form-input"
                              value={
                                formData.attendanceDetails?.[campus]?.[scheduleType]?.[name] || ""
                              }
                              onChange={(e) =>
                                handleAttendanceChange(
                                  campus,
                                  scheduleType,
                                  name,
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default CourseForm;
